import { supabase } from "@/integrations/supabase/client";

// Email template interfaces
export interface EmailTemplate {
  id: string;
  template_type: 'user_invitation' | 'partner_invitation' | 'proposal_notification' | 'order_confirmation' | 'magic_link' | 'password_reset' | 'release_notification' | 'outage_alert' | 'outage_resolved' | 'camera_alert' | 'camera_image';
  name: string;
  subject_template: string;
  content_config: {
    greeting?: string;
    strapline?: string;
    intro?: string;
    description?: string;
    benefits?: string[];
    proposal_details?: string;
    cta_text?: string;
    footer?: string;
    magic_link_text?: string;
    reset_link_text?: string;
    security_notice?: string;
    logo_url?: string;
    logo_link_url?: string;
    heading?: string;
    subheading?: string;
    badge_text?: string;
    badge_background?: string;
    badge_text_color?: string;
    fallback_heading?: string;
    unsubscribe_text?: string;
    show_image?: boolean;
  };
  color_config: {
    primary_color: string;
    secondary_color: string;
    background_color: string;
    text_color: string;
    button_color: string;
    button_text_color: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  logo_url?: string;
  from_address?: string;
}

export interface EmailTemplateDB {
  id: string;
  template_type: string;
  name: string;
  subject_template: string;
  content_config: any;
  color_config: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  logo_url?: string;
  from_address?: string;
}

/**
 * Service for managing email templates
 */
export class EmailTemplateService {
  
  /**
   * Get all email templates
   */
  async getAllTemplates(): Promise<EmailTemplate[]> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .order('template_type', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }

    return data?.map(this.mapFromDB) || [];
  }

  /**
   * Get active templates by type
   */
  async getActiveTemplateByType(templateType: string): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('template_type', templateType)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching email template:', error);
      throw error;
    }

    return data ? this.mapFromDB(data) : null;
  }

  /**
   * Save a new email template
   */
  async saveTemplate(template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .insert({
        template_type: template.template_type,
        name: template.name,
        subject_template: template.subject_template,
        content_config: template.content_config,
        color_config: template.color_config,
        logo_url: template.logo_url,
        from_address: template.from_address,
        is_active: template.is_active
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving email template:', error);
      throw error;
    }

    return this.mapFromDB(data);
  }

  /**
   * Update an existing email template
   */
  async updateTemplate(
    id: string, 
    updates: Partial<Pick<EmailTemplate, 'name' | 'subject_template' | 'content_config' | 'color_config' | 'logo_url' | 'from_address' | 'is_active'>>
  ): Promise<EmailTemplate> {
    const { data, error } = await supabase
      .from('email_templates')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating email template:', error);
      throw error;
    }

    return this.mapFromDB(data);
  }

  /**
   * Delete an email template
   */
  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting email template:', error);
      throw error;
    }
  }

  /**
   * Set template as active (deactivates others of same type)
   */
  async setAsActive(id: string, templateType: string): Promise<void> {
    // First deactivate all templates of this type
    await supabase
      .from('email_templates')
      .update({ is_active: false })
      .eq('template_type', templateType);

    // Then activate the selected one
    const { error } = await supabase
      .from('email_templates')
      .update({ is_active: true })
      .eq('id', id);

    if (error) {
      console.error('Error setting template as active:', error);
      throw error;
    }
  }

  /**
   * Process template with variables
   */
  processTemplate(template: EmailTemplate, variables: Record<string, any>): {
    subject: string;
    content: EmailTemplate['content_config'];
  } {
    const processText = (text: string): string => {
      return Object.entries(variables).reduce((result, [key, value]) => {
        const placeholder = `{{${key}}}`;
        return result.replace(new RegExp(placeholder, 'g'), String(value || ''));
      }, text);
    };

    const processedContent = { ...template.content_config };
    
    // Process all text fields in content_config
    Object.keys(processedContent).forEach(key => {
      const value = processedContent[key as keyof typeof processedContent];
      if (typeof value === 'string') {
        (processedContent as any)[key] = processText(value);
      }
    });

    return {
      subject: processText(template.subject_template),
      content: processedContent
    };
  }

  /**
   * Get available variables for each template type
   */
  getAvailableVariables(templateType: string): { key: string; description: string }[] {
    const commonVariables = [
      { key: 'contactName', description: 'Name of the contact/recipient' },
      { key: 'organizationName', description: 'Name of the organization' }
    ];

    switch (templateType) {
      case 'camera_alert':
      case 'camera_image':
        return [
          { key: 'camera_name', description: 'Camera name' },
          { key: 'ai_subject', description: 'AI result subject (or fallback heading)' },
          { key: 'organisation', description: 'Brand / organisation name' }
        ];
      case 'user_invitation':
        return [
          ...commonVariables,
          { key: 'inviterName', description: 'Name of the person sending the invitation' },
          { key: 'inviteLink', description: 'Direct link to accept the invitation' }
        ];
      
      case 'partner_invitation':
        return [
          { key: 'partnerName', description: 'Name of the partner being invited' },
          { key: 'inviteUrl', description: 'Direct link to accept the partnership' },
          { key: 'invitedByName', description: 'Name of the person sending the invitation' },
          { key: 'invitationToken', description: 'Unique invitation token' },
          { key: 'partnerId', description: 'Partner ID for tracking' },
          { key: 'baseUrl', description: 'Base URL of the application' }
        ];
      
      case 'proposal_notification':
        return [
          ...commonVariables,
          { key: 'totalCameras', description: 'Number of cameras in the proposal' },
          { key: 'totalImages', description: 'Number of images in the proposal' },
          { key: 'proposalValue', description: 'Total value of the proposal' },
          { key: 'proposalLink', description: 'Direct link to view the proposal' }
        ];
      
      case 'order_confirmation':
        return [
          ...commonVariables,
          { key: 'orderReference', description: 'Order reference number' },
          { key: 'dealTitle', description: 'Title of the order/deal' },
          { key: 'totalCameras', description: 'Number of cameras in the order' },
          { key: 'totalImages', description: 'Number of images/credits in the order' },
          { key: 'orderValue', description: 'Total value of the order' },
          { key: 'orderStatus', description: 'Current status of the order' },
          { key: 'trackingUrl', description: 'Link to track order status' },
          { key: 'estimatedDelivery', description: 'Estimated delivery date' },
          { key: 'assigneeName', description: 'Staff/partner handling the order' }
        ];
      
      case 'magic_link':
        return [
          { key: 'userEmail', description: 'Email address of the user' },
          { key: 'magicLinkUrl', description: 'Direct link to sign in with magic link' },
          { key: 'expiresIn', description: 'How long the magic link is valid for' }
        ];
      
      case 'password_reset':
        return [
          { key: 'userEmail', description: 'Email address of the user' },
          { key: 'resetLinkUrl', description: 'Direct link to reset password' },
          { key: 'expiresIn', description: 'How long the reset link is valid for' }
        ];
      
      case 'release_notification':
        return [
          { key: 'version', description: 'Release version number' },
          { key: 'releaseTitle', description: 'Title of the release' },
          { key: 'releaseDescription', description: 'Description of the release' },
          { key: 'featureCount', description: 'Number of features in this release' },
          { key: 'bugFixCount', description: 'Number of bug fixes in this release' },
          { key: 'releaseLink', description: 'Link to view full release details' }
        ];
      
      case 'outage_alert':
        return [
          { key: 'outageTitle', description: 'Title of the outage' },
          { key: 'outageType', description: 'Type of outage (maintenance, etc.)' },
          { key: 'severity', description: 'Severity level' },
          { key: 'description', description: 'Outage description' },
          { key: 'affectedServices', description: 'List of affected services' },
          { key: 'scheduledStart', description: 'Scheduled start time (empty if not set)' },
          { key: 'scheduledEnd', description: 'Scheduled end time (empty if not set)' },
          { key: 'scheduledStartSection', description: 'Full labeled scheduled start section (only renders if set)' },
          { key: 'scheduledEndSection', description: 'Full labeled scheduled end section (only renders if set)' },
          { key: 'affectedServicesSection', description: 'Full labeled affected services section (only renders if set)' },
          { key: 'contactName', description: 'Recipient name' }
        ];
      
      case 'outage_resolved':
        return [
          { key: 'outageTitle', description: 'Title of the outage' },
          { key: 'resolutionNotes', description: 'Notes about resolution' },
          { key: 'actualStart', description: 'When the outage started (empty if not set)' },
          { key: 'actualEnd', description: 'When the outage was resolved (empty if not set)' },
          { key: 'actualStartSection', description: 'Full labeled actual start section (only renders if set)' },
          { key: 'actualEndSection', description: 'Full labeled actual end section (only renders if set)' },
          { key: 'resolutionNotesSection', description: 'Full labeled resolution notes section (only renders if set)' },
          { key: 'contactName', description: 'Recipient name' }
        ];
      
      default:
        return commonVariables;
    }
  }

  /**
   * Generate mock data for preview
   */
  getMockData(templateType: string): Record<string, any> {
    const baseMock = {
      contactName: 'John Smith',
      organizationName: 'Acme Construction Ltd'
    };

    switch (templateType) {
      case 'user_invitation':
        return {
          ...baseMock,
          inviterName: 'Sarah Johnson',
          inviteLink: 'https://app.snapshot.live/invite/sample-token'
        };
      
      case 'partner_invitation':
        return {
          partnerName: 'Sarah Johnson',
          inviteUrl: 'https://app.snapshot.live/partner-invite?token=sample-token&partner_id=sample-id',
          invitedByName: 'Michael Chen',
          invitationToken: 'sample-token-12345',
          partnerId: 'partner-abc-123',
          baseUrl: 'https://app.snapshot.live'
        };
      
      case 'proposal_notification':
        return {
          ...baseMock,
          totalCameras: '5',
          totalImages: '1,500',
          proposalValue: '2,450.00',
          proposalLink: 'https://app.snapshot.live/proposal/sample-proposal'
        };
      
      case 'order_confirmation':
        return {
          ...baseMock,
          orderReference: 'ORD-2024-001',
          dealTitle: 'Construction Site Monitoring - Phase 1',
          totalCameras: '5',
          totalImages: '1,500',
          orderValue: '2,450.00',
          orderStatus: 'waiting_for_payment',
          trackingUrl: 'https://app.snapshot.live/dashboard/admin/orders',
          estimatedDelivery: '3-5 business days',
          assigneeName: 'Sarah Johnson'
        };
      
      case 'magic_link':
        return {
          userEmail: 'john.smith@example.com',
          magicLinkUrl: 'https://app.snapshot.live/auth?magic_link_token=sample-token',
          expiresIn: '1 hour'
        };
      
      case 'password_reset':
        return {
          userEmail: 'john.smith@example.com',
          resetLinkUrl: 'https://app.snapshot.live/auth?reset_token=sample-token',
          expiresIn: '1 hour'
        };
      
      case 'release_notification':
        return {
          version: 'v2.5.0',
          releaseTitle: 'Enhanced Device Management & Performance Improvements',
          releaseDescription: 'This release includes major improvements to device management and several performance enhancements.',
          featureCount: '5',
          bugFixCount: '8',
          releaseLink: 'https://app.snapshot.live/dashboard/info'
        };
      
      case 'outage_alert':
        return {
          outageTitle: 'Scheduled Maintenance - Camera Sync Service',
          outageType: 'Scheduled Maintenance',
          severity: 'Medium',
          description: 'We will be performing scheduled maintenance on our camera synchronization service to improve performance and reliability.',
          affectedServices: 'Camera Sync, Live Feed, Image Upload',
          scheduledStart: '2025-02-01 02:00 AM UTC',
          scheduledEnd: '2025-02-01 06:00 AM UTC',
          contactName: 'John Smith'
        };
      
      case 'outage_resolved':
        return {
          outageTitle: 'Camera Sync Service Maintenance Complete',
          resolutionNotes: 'All systems have been successfully updated and are now operating normally. Camera synchronization times have improved by 40%.',
          actualStart: '2025-02-01 02:00 AM UTC',
          actualEnd: '2025-02-01 05:30 AM UTC',
          contactName: 'John Smith'
        };
      
      default:
        return baseMock;
    }
  }

  /**
   * Map database record to EmailTemplate interface
   */
  private mapFromDB(data: EmailTemplateDB): EmailTemplate {
    return {
      id: data.id,
      template_type: data.template_type as EmailTemplate['template_type'],
      name: data.name,
      subject_template: data.subject_template,
      content_config: data.content_config,
      color_config: data.color_config,
      is_active: data.is_active,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by,
      logo_url: (data as any).logo_url,
      from_address: (data as any).from_address
    };
  }
}

// Export singleton instance
export const emailTemplateService = new EmailTemplateService();